// AuthContext.js
import Cookies from 'js-cookie';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { resetGlobalState, updateGlobalState } from '../../state/global/slice.tsx';
import { QueryClient } from '@tanstack/react-query';
import { persistor } from '../../state/global/store.tsx';
import api from '../../api/api';
import { auth } from '../../config/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const AuthContext = createContext();

// Firebase creates these IndexedDB databases internally.
// signOut(auth) already clears auth state from them via Firebase's own API.
// Force-deleting them causes the NEXT signInWithPopup to race against
// Firebase recreating a missing database → auth/network-request-failed.
const FIREBASE_INDEXED_DB_NAMES = [
  'firebaseLocalStorageDb',     // Firebase Auth v9+ persistence
  'firebaseLocalStorage',       // Firebase Auth v8 persistence
  'firebase-installations-database',
  'firebase-messaging-database',
  'firebase-heartbeat-database',
];

// Cross-tab logout signalling. Unlike the Firebase listener below, this works
// for every auth method (email/password users have no Firebase session) and
// does not depend on the cookie or localStorage that logout() is about to wipe.
const AUTH_CHANNEL = 'proschool-auth';
const LOGOUT_SIGNAL_KEY = 'auth_logout_signal';

// One-shot flag set on a fresh login; the student dashboard consumes it to
// decide whether to show the subject-only popup (not on reloads/revisits).
export const JUST_LOGGED_IN_KEY = 'just_logged_in';

// Session cookie lifetime in days. A shorter window reduces how long a stolen
// token stays usable; raising or lowering it is a product call about how often
// users are asked to sign in again.
const AUTH_COOKIE_DAYS = 7;

export function AuthProvider({ children }) {
    const dispatch = useDispatch();

    // Prevents the onAuthStateChanged cross-tab listener from reacting
    // to a logout that THIS tab intentionally initiated.
    const isLoggingOut = useRef(false);

    const updateState = (key, value) => {
        dispatch(updateGlobalState({ key: key, value: value }));
    };

    const [token, setToken] = useState(Cookies.get('auth_token') || null);

    const authChannelRef = useRef(null);

    // Latest token, readable from the listeners below without re-subscribing
    // them on every change.
    const hasSessionRef = useRef(Boolean(Cookies.get('auth_token')));
    useEffect(() => {
        hasSessionRef.current = Boolean(token);
    }, [token]);

    // Session teardown shared by the local logout and the remote (other tab)
    // one, so a tab logged out from elsewhere ends up in the same state.
    const clearLocalSession = () => {
        setToken(null);
        dispatch(resetGlobalState());
        sessionStorage.clear();
        localStorage.clear();
        Cookies.remove('auth_token');
        persistor.purge();
    };

    // Runs in tabs that did NOT initiate the logout.
    const handleRemoteLogout = () => {
        if (isLoggingOut.current) return;  // this tab started it, or already handled
        // A tab that was never logged in (sitting on /login or a public page)
        // has nothing to tear down — don't wipe its storage or navigate it.
        if (!hasSessionRef.current) return;
        isLoggingOut.current = true;       // de-dupes channel + storage + visibility
        clearLocalSession();
        window.location.replace('/login');
    };

    const broadcastLogout = () => {
        // Fire both: the channel is instant, the storage write covers browsers
        // without BroadcastChannel (Safari < 15.4). Receivers de-dupe.
        try {
            authChannelRef.current?.postMessage({ type: 'logout' });
        } catch (e) {
            console.warn('Logout broadcast failed:', e?.message);
        }
        try {
            localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
        } catch (e) {
            console.warn('Logout signal write failed:', e?.message);
        }
    };

    useEffect(() => {
        let channel = null;

        if ('BroadcastChannel' in window) {
            channel = new BroadcastChannel(AUTH_CHANNEL);
            channel.onmessage = (event) => {
                if (event?.data?.type === 'logout') handleRemoteLogout();
            };
            authChannelRef.current = channel;
        }

        // `storage` fires only in OTHER tabs — exactly the audience we want.
        // Scoped to our own key so unrelated localStorage.clear() calls
        // elsewhere in the app can't trigger a logout cascade.
        const onStorage = (event) => {
            if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) handleRemoteLogout();
        };

        // Safety net for tabs that were frozen or discarded and missed the
        // broadcast. Cookies are shared browser-wide, so a missing auth_token
        // means some other tab logged out while this one was asleep.
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            if (!Cookies.get('auth_token')) handleRemoteLogout();
        };

        window.addEventListener('storage', onStorage);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            channel?.close();
            authChannelRef.current = null;
            window.removeEventListener('storage', onStorage);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchSchoolModules = async (newToken) => {
        try {
            const response = await api.get('/api/my-school/modules/', {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${newToken}`,
                },
            });
            const modules = response?.data?.enabled_modules || [];
            updateState('enabled_modules', modules);
            return modules;
        } catch (error) {
            console.warn('Failed to load school modules:', error?.message || error);
            return null;
        }
    };

    const login = (newToken, role, email, school_id = null) => {
        setToken(newToken);
        updateState("token", newToken);
        updateState("role", role);
        updateState("email", email.toLowerCase());
        updateState("school_id", school_id);
        // secure: the cookie is never sent over plain HTTP (skipped on localhost,
        //   where there is no TLS and the attribute would stop it being set at all).
        // sameSite strict: the cookie is not attached to cross-site requests,
        //   which removes the CSRF vector on every state-changing endpoint.
        // expires: still 7 days — shortening it logs users out sooner, so that is
        //   a product decision; reduce AUTH_COOKIE_DAYS when you are ready.
        Cookies.set('auth_token', newToken, {
            expires: AUTH_COOKIE_DAYS,
            secure: window.location.protocol === 'https:',
            sameSite: 'strict',
        });
        try {
            sessionStorage.setItem(JUST_LOGGED_IN_KEY, 'true');
        } catch (e) {
            console.warn('Login flag write failed:', e?.message);
        }
        fetchSchoolModules(newToken);
    };

    // Re-fetch school modules on every app mount when a user is already logged in.
    useEffect(() => {
        const existingToken = token || Cookies.get('auth_token');
        if (existingToken) {
            fetchSchoolModules(existingToken);
        }
    }, []);

    // Cross-tab logout sync via Firebase's onAuthStateChanged.
    //
    // When Tab A calls signOut(auth) in logout(), Firebase fires
    // onAuthStateChanged(null) in EVERY open tab. Tab B catches it here,
    // sees it still has an active cookie, and clears its own stale state.
    // This eliminates the ghost Firebase instance in Tab B that was
    // conflicting with Tab A's next signInWithPopup call.
    //
    // The 'initialized' flag skips the very first fire on mount:
    // email/password users have no Firebase session, so their first fire
    // is always null — without this skip they'd be force-logged-out on load.
    useEffect(() => {
        let initialized = false;

        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (!initialized) {
                initialized = true;
                return; // skip initial state — not a cross-tab event
            }

            if (!firebaseUser && !isLoggingOut.current) {
                const activeToken = Cookies.get('auth_token');
                if (activeToken) {
                    // Another tab logged out — sync this tab's state. Now a
                    // secondary net: the broadcast above usually lands first,
                    // and handleRemoteLogout de-dupes whichever arrives second.
                    handleRemoteLogout();
                }
            }
        });

        return () => unsubscribe();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const logout = async (redirectUrl, isLogout = false) => {
        isLoggingOut.current = true;

        // Several callers omit the redirect. Without this fallback the final
        // line becomes location.replace(undefined), which the browser coerces
        // to the string "undefined" and resolves RELATIVE to the current page —
        // e.g. /course/836063/view lands on /course/836063/undefined.
        const target =
            typeof redirectUrl === 'string' && redirectUrl.trim()
                ? redirectUrl.trim()
                : '/login';

        // Signal other tabs FIRST. Receivers must not depend on the cookie or
        // localStorage this function is about to clear.
        broadcastLogout();

        clearSockets();

        // Step 1: Tell Firebase to sign out via its own API.
        // This cleanly updates firebaseLocalStorageDb (sets user → null)
        // and broadcasts onAuthStateChanged(null) to all other open tabs.
        // The database itself is preserved and left in a valid empty state.
        try {
            await signOut(auth);
        } catch (e) {
            console.warn('Firebase signOut warning:', e?.message);
        }

        setToken(null);
        dispatch(resetGlobalState());
        localStorage.removeItem("student_formState");
        localStorage.removeItem("teacher_formState");

        sessionStorage.clear();
        localStorage.clear();
        Cookies.remove('auth_token');
        persistor.purge();

        if ('caches' in window) {
            caches.keys().then((cacheNames) => {
                cacheNames.forEach((cacheName) => caches.delete(cacheName));
            }).catch((error) => {
                console.error('Error fetching cache names:', error);
            });
        }

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                registrations.forEach((registration) => registration.unregister());
            });
        }

        indexedDB.databases().then((databases) => {
            databases.forEach((db) => {
                if (!FIREBASE_INDEXED_DB_NAMES.includes(db.name)) {
                    indexedDB.deleteDatabase(db.name);
                }
            });
        });

        window.location.replace(isLogout ? `${target}?logout=true` : target);
    };

    const clearSockets = async () => {
        let newToken = token;
        try {
            await api.post(
                "/auth/clear-websockets/",
                {},
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Token ${newToken}`,
                    },
                }
            );
        } catch (error) {
            return null;
        }
    };

    return (
        <AuthContext.Provider value={{ token, login, logout, isLoggingOut }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}